import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Row, Col, Card, Container, Toast } from 'react-bootstrap';

import { AccountContext } from '../../Accounts';
import { DataTypeContext } from '../../DataTypes';

const ProfileFormReview = ({ onSubmit, onCancel, onDone, formData, isLoading, isEdit, isUpdateSuccess }) => {
    const { firstName, lastName, dob, age } = useContext(AccountContext);
    const { t } = useTranslation();
    const { dataTypes } = useContext(DataTypeContext);
    const [showToast, setShowToast] = useState(false);

    useEffect(() => {
        if (isUpdateSuccess) setShowToast(true);
    }, [isUpdateSuccess]);
    const enabledFields = dataTypes.enabledFields || [];
    const ethnicityLabel = enabledFields.includes('isHispanicLatino') ? t('intake.ethnicities_alternate') : t('intake.ethnicities');

    const data = useMemo(() => {
        if (dataTypes && formData) {
            const householdMembers = (formData?.hhMembers || []).map((member) => {
                return {
                    firstName: member?.firstName,
                    lastName: member?.lastName,
                    dob: member?.dob,
                    age: member?.age,
                    gender: dataTypes?.genders?.find((type) => type.name === member?.hhmGender)?.label,
                    relationship: dataTypes?.relationships?.find((type) => type.name === member?.hhmRelationship)?.label,
                    maritalStatus: null,
                };
            });

            const fullAddress = [];
            if (!formData?.noFixedAddress && !formData?.addressDeclinedToAnswer) {
                if (enabledFields.includes('addressLine1') && formData?.addressLine1) fullAddress.push(formData.addressLine1);
                if (enabledFields.includes('addressLine2') && formData?.addressLine2) fullAddress.push(formData.addressLine2);
                if (enabledFields.includes('city') && formData?.city) fullAddress.push(formData.city);
                if (enabledFields.includes('county') && formData?.county) fullAddress.push(formData.county);
                if (enabledFields.includes('state') && formData?.state) fullAddress.push(formData.state);
                if (enabledFields.includes('zipCode') && formData?.zip) fullAddress.push(formData.zip);
            }

            return {
                ...formData,
                name: `${formData?.firstName || firstName || ''} ${formData?.lastName || lastName || ''}`.trim(),
                dob: formData?.dob || dob,
                age: formData?.age || age,
                gender: dataTypes?.genders?.find((type) => type.name === formData?.gender)?.label,
                maritalStatus: dataTypes?.maritalStatuses?.find((type) => type.name === formData?.maritalStatus)?.label,
                hhMembers: householdMembers,
                dietaryConsiderationTypes: formData?.dietaryConsiderationTypes
                    ?.map((type) => dataTypes?.dietaryConsiderationTypes.find((t) => t.name === type)?.label)
                    ?.join(', '),
                housingType: dataTypes?.housingTypes?.find((type) => type.name === formData?.housingType)?.label,
                referredBy: dataTypes?.referredBy?.find((type) => type.name === formData?.referredBy)?.label,
                selfIdentificationType: formData?.selfIdentificationType
                    ?.map((type) => dataTypes?.selfIdentificationTypes.find((t) => t.name === type)?.label)
                    ?.join(', '),
                ethnicity: formData?.ethnicityType
                    ?.map((type) => dataTypes?.ethnicities.find((t) => t.name === type)?.label)
                    ?.join(', '),
                isHispanicLatino: dataTypes?.isHispanicLatino?.find((type) => type.name === formData?.isHispanicLatino)?.label,
                educationType: dataTypes?.educationTypes?.find((type) => type.name === formData?.educationType)?.label,
                employmentType: dataTypes?.employmentTypes?.find((type) => type.name === formData?.employmentType)
                    ?.label,
                incomeType: dataTypes?.income_sourceTypes?.find((type) => type.name === formData?.incomeType)?.label,
                languageTypes: formData?.languageTypes?.join(', '),
                address: formData?.noFixedAddress
                    ? t('address.noFixedAddress')
                    : formData?.addressDeclinedToAnswer
                    ? t('address.addressDeclinedToAnswer')
                    : fullAddress.join(', '),
                socialProgramTypes: formData?.socialProgramTypes
                    ?.map((type) => dataTypes?.socialProgramTypes.find((t) => t.name === type)?.label)
                    ?.join(', '),
            };
        }
    }, [dataTypes, enabledFields, dob, age, firstName, formData, lastName, t]);

    const notFilledValue = t('intake.notFilledValue');
    const displayValue = (value) => (value !== undefined && value !== null && value !== '' ? value : notFilledValue);

    return (
        <Col md={6} sm={12} className="mx-auto">
            <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999 }}>
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    autohide
                    delay={4000}
                    className="bg-success border-success"
                >
                    <Toast.Header className="bg-success border-success text-white">
                        <strong className="mr-auto">{t('core.action.success')}</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">
                        {t('core.updateSuccess')}
                    </Toast.Body>
                </Toast>
            </div>
            <div className="card shadow-sm rounded p-4">
                <h3 className="text-center">{t('intake.reviewHeading')}</h3>
                <p className="text-center text-muted mb-4">
                    {t('intake.reviewText')}
                </p>

                <h4 className="h5 mb-3">{t('intake.personalInformationHeading')}</h4>
                <Row className="mb-2">
                    <Col md={6}>{t('intake.name')}:</Col>
                    <Col md={6}>{displayValue(data?.name)}</Col>
                </Row>
                {enabledFields.includes('dateOfBirth') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('personal.dob')}:</Col>
                        <Col md={6}>{data?.dob ? moment(data.dob).format(dataTypes.dateFormat.toUpperCase()) : notFilledValue}</Col>
                    </Row>
                )}
                {enabledFields.includes('age') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('personal.age')}:</Col>
                        <Col md={6}>{displayValue(data?.age)}</Col>
                    </Row>
                )}
                {enabledFields.includes('gender') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('intake.gender')}:</Col>
                        <Col md={6}>{displayValue(data?.gender)}</Col>
                    </Row>
                )}
                {enabledFields.includes('maritalStatus') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('intake.maritalStatus')}:</Col>
                        <Col md={6}>{displayValue(data?.maritalStatus)}</Col>
                    </Row>
                )}
                {enabledFields.includes('isHispanicLatino') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('intake.isHispanicLatino')}:</Col>
                        <Col md={6}>{displayValue(data?.isHispanicLatino)}</Col>
                    </Row>
                )}
                {enabledFields.includes('ethnicity') && (
                    <Row className="mb-2">
                        <Col md={6}>{ethnicityLabel}:</Col>
                        <Col md={6}>{displayValue(data?.ethnicity)}</Col>
                    </Row>
                )}
                {enabledFields.includes('selfIdentificationType') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('intake.selfIdentifiesAs')}:</Col>
                        <Col md={6}>{displayValue(data?.selfIdentificationType)}</Col>
                    </Row>
                )}
                {enabledFields.includes('educationType') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('intake.education')}:</Col>
                        <Col md={6}>{displayValue(data?.educationType)}</Col>
                    </Row>
                )}
                {enabledFields.includes('employmentType') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('intake.employment')}:</Col>
                        <Col md={6}>{displayValue(data?.employmentType)}</Col>
                    </Row>
                )}
                {enabledFields.includes('incomeType') && (
                    <Row className="mb-3">
                        <Col md={6}>{t('intake.incomeType')}:</Col>
                        <Col md={6}>{displayValue(data?.incomeType)}</Col>
                    </Row>
                )}

                <hr className="my-4" />

                <h4 className="h5 mb-3">{t('intake.householdDetailsHeading')}</h4>
                {['addressLine1', 'addressLine2', 'city', 'county', 'state', 'zipCode'].some((field) =>
                    enabledFields.includes(field)
                ) && (
                    <Row className="mb-2">
                        <Col md={6}>{t('address.heading')}:</Col>
                        <Col md={6}>{displayValue(data?.address)}</Col>
                    </Row>
                )}
                {enabledFields.includes('housingType') && (
                    <Row className="mb-2">
                        <Col md={6}>{t('intake.housingType')}:</Col>
                        <Col md={6}>{displayValue(data?.housingType)}</Col>
                    </Row>
                )}
                {enabledFields.includes('languageTypes') && (
                    <Row className="mb-3">
                        <Col md={6}>{t('intake.languageTypes')}:</Col>
                        <Col md={6}>{displayValue(data?.languageTypes)}</Col>
                    </Row>
                )}

                <hr className="my-4" />

                <h4 className="h5 mb-3">{t('intake.hhMemberHeading')}</h4>
                <Card className="mb-3">
                    <Card.Body className="py-3">
                        <Row className="mb-2">
                            <Col md={6}>{t('intake.name')}:</Col>
                            <Col md={6}>{displayValue(data?.name)}</Col>
                        </Row>
                        {enabledFields.includes('dateOfBirth') && (
                            <Row className="mb-2">
                                <Col md={6}>{t('personal.dob')}:</Col>
                                <Col md={6}>{data?.dob ? moment(data.dob).format(dataTypes.dateFormat.toUpperCase()) : notFilledValue}</Col>
                            </Row>
                        )}
                        {enabledFields.includes('age') && (
                            <Row className="mb-2">
                                <Col md={6}>{t('personal.age')}:</Col>
                                <Col md={6}>{displayValue(data?.age)}</Col>
                            </Row>
                        )}
                        {enabledFields.includes('gender') && (
                            <Row className="mb-2">
                                <Col md={6}>{t('intake.gender')}:</Col>
                                <Col md={6}>{displayValue(data?.gender)}</Col>
                            </Row>
                        )}
                        {enabledFields.includes('maritalStatus') && (
                            <Row>
                                <Col md={6}>{t('intake.maritalStatus')}:</Col>
                                <Col md={6}>{displayValue(data?.maritalStatus)}</Col>
                            </Row>
                        )}
                    </Card.Body>
                </Card>

                {(data?.hhMembers || []).map((member, index) => (
                    <Card className="mb-3" key={`hh-review-${index}`}>
                        <Card.Body className="py-3">
                            <Row className="mb-2">
                                <Col md={6}>{t('intake.name')}:</Col>
                                <Col md={6}>
                                    {`${member.firstName || ''} ${member.lastName || ''}`.trim() || notFilledValue}
                                </Col>
                            </Row>
                            {enabledFields.includes('dateOfBirth') && (
                                <Row className="mb-2">
                                    <Col md={6}>{t('personal.dob')}:</Col>
                                    <Col md={6}>
                                        {member?.dob
                                            ? moment(member.dob).format(dataTypes.dateFormat.toUpperCase())
                                            : notFilledValue}
                                    </Col>
                                </Row>
                            )}
                            {enabledFields.includes('age') && (
                                <Row className="mb-2">
                                    <Col md={6}>{t('personal.age')}:</Col>
                                    <Col md={6}>{displayValue(member?.age)}</Col>
                                </Row>
                            )}
                            {enabledFields.includes('gender') && (
                                <Row className="mb-2">
                                    <Col md={6}>{t('intake.gender')}:</Col>
                                    <Col md={6}>{displayValue(member?.gender)}</Col>
                                </Row>
                            )}
                            {enabledFields.includes('relationship') && (
                                <Row>
                                    <Col md={6}>{t('intake.relationships')}:</Col>
                                    <Col md={6}>{displayValue(member?.relationship)}</Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>
                ))}

                <hr className="my-4" />

                <Card className="bg-light border mb-4">
                    <Card.Body className="text-muted">
                        {t('intake.reviewPrivacyNotice')}
                    </Card.Body>
                </Card>

                <Row>
                    <Col md={6} className="mb-2 mb-md-0">
                        <Button variant="outline-secondary" id="backButton" disabled={isLoading} onClick={onCancel} className="w-100">
                            {t('core.action.back')}
                        </Button>
                    </Col>
                    <Col md={6}>
                        <Button
                            variant="primary"
                            id="nextButton"
                            disabled={isLoading}
                            onClick={isUpdateSuccess ? onDone : onSubmit}
                            className="w-100"
                        >
                            {isLoading ? t('core.action.loading') : isUpdateSuccess ? t('core.action.done') : t('core.action.save')}
                        </Button>
                    </Col>
                </Row>
            </div>
            {isEdit && (
                <p className="text-center mt-3 mb-0">
                    <Link to="/dashboard">{t('dashboard.return')}</Link>
                </p>
            )}
        </Col>
    );
};

export default ProfileFormReview;
